import React, { ComponentType } from "react";
import { IntlShape, useIntl } from "react-intl";

import type { ILazyPage } from "./lazyPage";

export interface IWrappedComponentProps {
    intl: IntlShape,
}

/**
 * What react-intl's injectIntl did, and what react-intl stopped shipping: hand the intl
 * object to a class component, which cannot call useIntl itself.
 */
const injectIntl = <P extends object>(Component: ComponentType<P>) => {
    const WithIntl = (props: Omit<P, "intl"> & { children?: React.ReactNode }) => {
        const intl = useIntl();
        // Whether the wrapped component declares intl is its own business: connect() and
        // the other wrappers it is used with hand their props through as any.
        return <Component {...({ ...props, intl } as P)} />;
    };
    WithIntl.displayName = `injectIntl(${Component.displayName || Component.name || "Component"})`;
    // lazyPage puts moduleId and preload on the component as statics, and preloadRoutes
    // looks for them there. The injectIntl this replaces carried unknown statics over; a
    // wrapped page that lost them would never be loaded before the server rendered it.
    const lazy = Component as Partial<ILazyPage>;
    if (typeof lazy.preload === "function") {
        (WithIntl as Partial<ILazyPage>).preload = lazy.preload;
        (WithIntl as Partial<ILazyPage>).moduleId = lazy.moduleId;
    }
    return WithIntl;
};

export default injectIntl;
